import { getCsrfToken } from 'utils/PageVariables';
import { submitHiddenFormPost } from 'utils/submitHiddenFormPost';
import { GetCustomReportSchemaContract } from '../../schemas/GetCustomReportSchema';

const DOWNLOAD_URL = '/v2/custom-report/download';

/**
 * Download the report CSV.
 *
 * Legacy report types (supportsFilteredDownload falsy) always use the plain
 * GET full export, exactly as before filtered downloads existed. Only report
 * types that opt in via supportsFilteredDownload POST their active filters:
 * filter values cannot ride a GET URL (up to 10k people ids), so they are
 * POSTed as a hidden form instead. With no active filters even opted-in types
 * stay a plain GET (browser-native download).
 *
 * Keyed off supportsFilteredDownload, NOT supportsCustomFieldFilter: the latter
 * is the opt-in for the reserved hris_people_ids filter and also drives the
 * HRIS custom-field filter UI, so reusing it here coupled unrelated behaviour.
 */
export const downloadCustomReport = ({
  type,
  activeFilters,
  supportsFilteredDownload = false,
}: {
  type: string | null;
  activeFilters: Record<string, string>;
  supportsFilteredDownload?: boolean;
}): void => {
  if (!supportsFilteredDownload || Object.keys(activeFilters).length === 0) {
    window.open(`${DOWNLOAD_URL}?type=${type}`, '_blank');
    return;
  }

  submitHiddenFormPost({
    url: DOWNLOAD_URL,
    fields: {
      type: type ?? '',
      // Same token every v2 API call sends as the `csrf` header
      // (window._C_S_R_F_). A form POST cannot set headers, so it rides
      // as a field for the backend to verify against v1()->getCSRFToken().
      csrf: getCsrfToken() ?? '',
      ...Object.fromEntries(
        Object.entries(activeFilters).map(([key, value]) => [`filters[${key}]`, value]),
      ),
    },
    target: '_blank',
  });
};

/**
 * Filters out hidden columns and returns an array of their keys
 */
export const filterHiddenColumns = (
  columns?: GetCustomReportSchemaContract['response']['columns']
): string[] => {
  if (!columns) {
    return [];
  }
  
  return columns
    .filter((column) => column.config?.isHidden)
    .map((column) => column.key) || [];
}; 